import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../lib/api";

/** Loading, error (by error code, ADR 0017 rule 7) and empty states for a query. */
export function QueryState<T>({
  query,
  emptyKey,
  children,
}: {
  query: UseQueryResult<T>;
  emptyKey?: string;
  children: (data: T) => ReactNode;
}) {
  const { t } = useTranslation();
  if (query.isPending) {
    return (
      <div className="py-16 text-center text-[14px] text-ink-2" aria-busy="true">
        {t("common.loading")}
      </div>
    );
  }
  if (query.isError) {
    const code = query.error instanceof ApiError ? query.error.code : "unknown";
    return (
      <div role="alert" className="rounded-[var(--radius-card)] border border-line bg-surface p-6 text-[14px] text-ink">
        {t(`error.${code}`, { defaultValue: t("error.unknown") })}
        <button type="button" className="ml-3 text-violet-text underline" onClick={() => void query.refetch()}>
          {t("common.retry")}
        </button>
      </div>
    );
  }
  const data = query.data as T;
  if (emptyKey && Array.isArray(data) && data.length === 0) {
    return <div className="py-16 text-center text-[14px] text-ink-2">{t(emptyKey)}</div>;
  }
  return <>{children(data)}</>;
}
